import type { User } from 'oidc-client-ts';

/**
 * The Keycloak realm roles the backend knows. `app-superadmin` is a composite of the other two, so a
 * superadmin token carries all three.
 */
export type AppRole = 'app-user' | 'app-admin' | 'app-superadmin';

export const APP_ROLES: readonly AppRole[] = ['app-user', 'app-admin', 'app-superadmin'];

interface RealmAccessClaims {
  // biome-ignore lint/style/useNamingConvention: Keycloak claim name
  realm_access?: { roles?: unknown };
}

const isAppRole = (value: unknown): value is AppRole =>
  typeof value === 'string' && (APP_ROLES as readonly string[]).includes(value);

/** Decode the payload of a JWT without verifying it (the backend verifies; this is UI hinting). */
export const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = decodeURIComponent(
      Array.from(atob(padded), (char) => `%${char.charCodeAt(0).toString(16).padStart(2, '0')}`).join('')
    );
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
};

const rolesFromClaims = (claims: RealmAccessClaims | null | undefined): AppRole[] => {
  const roles = claims?.realm_access?.roles;
  return Array.isArray(roles) ? roles.filter(isAppRole) : [];
};

/**
 * Application roles of a signed-in user.
 *
 * Keycloak puts realm roles into the access token by default and into the ID token or the
 * userinfo response only when the realm is configured to. The profile is checked first and the
 * access token payload second, so the header renders the admin link regardless of that setting.
 */
export const rolesOfUser = (user: User | null | undefined): AppRole[] => {
  if (!user) return [];
  const fromProfile = rolesFromClaims(user.profile as RealmAccessClaims);
  if (fromProfile.length > 0) return fromProfile;
  return rolesFromClaims(user.access_token ? (decodeJwtPayload(user.access_token) as RealmAccessClaims | null) : null);
};

/** Display name for the header: full name, then username, then e-mail. */
export const displayNameOfUser = (user: User | null | undefined): string => {
  const profile = user?.profile;
  return profile?.name || profile?.preferred_username || profile?.email || '';
};

/**
 * The username the backend records as `author` / `updated_by`: `preferred_username`, else `sub`.
 * Used to show the caller's own rows as "vy".
 */
export const usernameOfUser = (user: User | null | undefined): string => {
  const profile = user?.profile;
  return profile?.preferred_username || profile?.sub || '';
};
